/**
 * Apertura.tsx — cargar los saldos con los que el cliente llega.
 *
 * Es la mitad de la puesta en marcha (E6.3) que **no** hace el operador. Ver
 * la cabecera de `db/migraciones/0030_puesta_en_marcha.sql`: cargar una
 * apertura es escribir en los libros, y quien lo hace tiene que ser quien
 * responde por esos saldos, no quien tramitó el alta.
 *
 * Hasta hoy esto solo existía como `npm run apertura:cargar`, o sea que un
 * cliente no podía terminar de ponerse en marcha sin nosotros.
 *
 * ── ▶ EL CUADRE QUE SE VE ACÁ ES UN ANTICIPO, NO UN VEREDICTO ─────────────
 *
 * Lo calcula `dinero.ts`, que es exacto, pero quien decide es la base. Está
 * para que nadie mande trescientas líneas y se entere al final de que
 * faltaban mil colones. Y cuando un importe no se puede leer, **dice que no
 * se puede anticipar** en vez de sumar un cero: un cuadre que sale de tratar
 * lo ilegible como cero es exactamente el error que cuadra y está mal.
 *
 * ── Y los importes se muestran TAL CUAL vinieron ──────────────────────────
 *
 * Sin separadores de miles y sin reformatear. Lo que se va a guardar es ese
 * texto, y enseñarlo bonito escondería justo lo que hay que revisar antes de
 * mandarlo: un `1,250.00` leído donde el archivo decía `1.250,00`.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIdioma } from '../nucleo/idioma.tsx';
import { anioYMesDeHoy } from '../nucleo/hoy.ts';
import { puedeAdministrar, puedeRegistrar, useSesion } from '../nucleo/sesion.tsx';
import {
  armarPlantilla, leerApertura, nombreDePlantilla, type LineaDeApertura,
} from '../nucleo/apertura.ts';
import { useCatalogo } from '../nucleo/catalogo.tsx';
import { diferencia, esCero, mostrar, sumar, type Importe } from '../nucleo/dinero.ts';
import {
  cargarApertura, declararSinSaldosPrevios, traerEstadoDeLaApertura, FalloDeApi,
} from '../datos/libros.ts';
import { DesdeBalance } from '../componentes/DesdeBalance.tsx';

/** Un importe del CSV que se puede sumar sin inventar nada. */
const CANONICO = /^-?\d+(\.\d+)?$/;

type Cuadre =
  | { que: 'sin_lineas' }
  | { que: 'ilegible'; cuenta: string }
  | { que: 'calculado'; debe: Importe; haber: Importe; cuadra: boolean; falta: Importe | null };

function anticipar(lineas: readonly LineaDeApertura[], moneda: string): Cuadre {
  if (lineas.length === 0) return { que: 'sin_lineas' };

  for (const l of lineas) {
    for (const v of [l.debe, l.haber]) {
      // ▶ Se mira el texto ANTES de sumarlo. Un importe que no tiene forma
      // canónica no vale cero: no se puede anticipar nada, y decirlo es la
      // respuesta honesta. La base lo va a rechazar igual, con su código.
      if (v !== undefined && !CANONICO.test(v)) return { que: 'ilegible', cuenta: l.cuenta };
    }
  }

  const debe = sumar(lineas.filter((l) => l.debe).map((l) => ({ valor: l.debe!, moneda })))
    ?? { valor: '0', moneda };
  const haber = sumar(lineas.filter((l) => l.haber).map((l) => ({ valor: l.haber!, moneda })))
    ?? { valor: '0', moneda };
  const falta = diferencia(debe, haber);

  return { que: 'calculado', debe, haber, cuadra: falta !== null && esCero(falta), falta };
}

export function Apertura() {
  const { t, idioma } = useIdioma();
  const { pedir, sesion } = useSesion();

  const escribe = puedeRegistrar(sesion);
  /* ▶ Declarar que no hay saldos anteriores es de ADMIN —`SB229`, 0064— y
     CARGAR la apertura no lo es. Dos permisos distintos, y esta pantalla usaba
     uno solo: un contador veía el botón y la base lo rechazaba. */
  const administra = puedeAdministrar(sesion);

  /**
   * Con que archivo viene. Empieza en `null` y NO en la plantilla.
   *
   * Un defecto aca seria de los que el cambio 79 llama indistinguibles de una
   * respuesta: la pantalla se abriria pidiendo un CSV con NUESTROS codigos, y
   * el cliente que trae el balance de su sistema no tiene forma de saber que
   * hay otro camino. Preguntar cuesta un clic; adivinar cuesta que suba un
   * archivo con codigos ajenos y no entienda por que se rechaza.
   */
  const [via, setVia] = useState<'plantilla' | 'balance' | null>(null);

  const [cargada, setCargada] = useState<boolean | null>(null);
  const [sinSaldos, setSinSaldos] = useState<string | null>(null);
  const [moneda, setMoneda] = useState<string | null>(null);
  // El año del NEGOCIO. Con el de UTC, un 31 de diciembre a las 18:01 de
  // Costa Rica esta pantalla proponía el 1 de enero del año siguiente — el
  // mismo defecto que la 0001 arregló en `provisionar_empresa`.
  const [fecha, setFecha] = useState(`${anioYMesDeHoy().anio}-01-01`);
  const [csv, setCsv] = useState('');
  const [fallo, setFallo] = useState<string | null>(null);
  const [numero, setNumero] = useState<string | null>(null);
  const [ocupado, setOcupado] = useState(false);

  /* ── La plantilla, armada con el catálogo de ESTA empresa ──────────────
   *
   * ▶ Sale del catálogo que ya está en memoria y no de una llamada nueva: el
   * contexto lo comparten tres pantallas y ésta es una de ellas.
   *
   * Y sale del catálogo de la empresa y NO de la plantilla modelo, que es lo
   * que separa una plantilla útil de una que miente: el contador pudo
   * renombrar la 4110 y colgar auxiliares bajo la 1102. El filtro —hoja y
   * activa— lo hace `armarPlantilla`, para que sea una sola regla. */
  const { cuentas } = useCatalogo();
  const cuentasDeMovimiento = cuentas.filter((c) => c.permiteMovimiento && c.activa);

  /**
   * Las dimensiones que el catálogo de ESTA empresa declara.
   *
   * ▶ Salen de las cuentas, no de una lista. `apertura.ts` tenía la suya y se
   * quedó corta dos veces sin que nada fallara —`modalidad` y `supplier`
   * nacieron después—: la columna no se leía, y una deuda de apertura sin
   * proveedor **no se puede pagar nunca**, porque el pago sí exige la
   * dimensión. Ver `COLUMNAS_PROPIAS`.
   */
  const dimensiones = useMemo(() => {
    const d = new Set<string>();
    for (const c of cuentas) for (const x of c.clasificacion) d.add(x);
    return [...d].toSorted();
  }, [cuentas]);

  function descargarPlantilla() {
    const csvPlantilla = armarPlantilla(cuentas, dimensiones);
    // `text/csv` con su charset: sin él, algunos navegadores lo guardan como
    // `.txt`. El BOM lo pone `armarPlantilla`, que es quien sabe por qué.
    const url = URL.createObjectURL(
      new Blob([csvPlantilla], { type: 'text/csv;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = nombreDePlantilla(sesion?.empresa.codigo ?? 'apertura');
    a.click();
    // Se suelta el objeto: sin esto, cada descarga deja el archivo entero en
    // memoria hasta que se recargue la página.
    URL.revokeObjectURL(url);
  }

  const mirar = useCallback(async () => {
    try {
      const e = await traerEstadoDeLaApertura(pedir);
      setCargada(e.cargada);
      setSinSaldos(e.sinSaldosPrevios ?? null);
      setMoneda(e.moneda);
    } catch {
      setCargada(null);
      setFallo('sb.conexion');
    }
  }, [pedir]);

  useEffect(() => { void mirar(); }, [mirar]);

  const lectura = useMemo(
    () => (csv.trim() === '' ? null : leerApertura(csv, dimensiones)),
    [csv, dimensiones]);
  const lineas = lectura?.ok ? lectura.lineas : [];
  const cuadre = useMemo(
    () => anticipar(lineas, moneda ?? 'CRC'), [lineas, moneda]);

  async function leerArchivo(f: File | null | undefined) {
    if (!f) return;
    setCsv(await f.text());
  }

  async function mandar() {
    setOcupado(true);
    setFallo(null);
    try {
      const n = await cargarApertura(pedir, { fecha, lineas });
      setNumero(n);
      setCsv('');
      await mirar();
    } catch (e) {
      const f = e instanceof FalloDeApi ? e : null;
      setFallo(f?.clase === 'conexion' ? 'sb.conexion'
        : f?.codigo ? `sb.${f.codigo}` : 'sb.unknown');
    } finally {
      setOcupado(false);
    }
  }

  /**
   * Declarar que no hay saldos anteriores.
   *
   * ▶ Usa la MISMA fecha del formulario, y no una propia. Es el mismo hecho por
   * los dos caminos —desde cuándo empiezan los libros de esta empresa—, y dos
   * campos para una fecha invitan a que discrepen.
   */
  async function declarar() {
    setOcupado(true);
    try {
      await declararSinSaldosPrevios(pedir, fecha);
      // Con esto la pantalla ya dibuja el estado declarado: `escribe` sale de la
      // sesion y no es estado, asi que no hay nada mas que apagar.
      setSinSaldos(fecha);
    } catch (e) {
      const f = e instanceof FalloDeApi ? e : null;
      setFallo(f?.codigo ? `sb.${f.codigo}` : 'sb.unknown');
    } finally {
      setOcupado(false);
    }
  }

  const fechaValida = /^\d{4}-\d{2}-\d{2}$/.test(fecha);
  const puedeMandar = escribe && !ocupado && fechaValida
    && lectura?.ok === true && lineas.length > 0;

  return (
    <div>
      {/* ▶ ESTA PANTALLA SE TITULABA «CONSOLA DE OPERADOR».
       *
       * Llevaba la clave `op.title` —el título de la consola— de cuando los
       * saldos de apertura se cargaban desde ahí. La pantalla se abrió al
       * cliente y el encabezado se quedó: un admin de su propia empresa
       * entraba a cargar su balance y leía, en letra grande, que estaba en una
       * consola de operador.
       *
       * Se vio abriendo la segunda empresa demo por el camino que hará el
       * cliente de verdad — el enlace «Cargar los saldos» de la puesta en
       * marcha—, que es el único sitio desde donde se llega acá.
       *
       * El título de la pantalla ya existía (`ap.title`) y estaba puesto en la
       * cabecera de una tarjeta que no decía nada más: se sube a su sitio, con
       * su bajada, como en las otras once pantallas. */}
      <div className="page-head">
        <div className="page-head__text">
          <h1>{t('ap.title')}</h1>
          <p className="page-head__sub">{t('ap.sub')}</p>
        </div>
      </div>

      {fallo && (
        <div className="alert alert--warn" style={{ marginBottom: 'var(--s6)' }}>
          <span>{t(fallo as never)}</span>
        </div>
      )}

      {/* ── Ya está cargada: no hay formulario, y se dice por qué ── */}
      {cargada === true && (
        <section className="card">
          <div className="card__head"><h3>{t('ap.already')}</h3></div>
          <p className="small">{t('ap.alreadyWhy')}</p>
          {numero && <p className="small mono">#{numero}</p>}
        </section>
      )}

      {/* ── Declarado que no hay nada anterior ────────────────────────────
          ▶ Es un estado propio, no «cargada» ni «pendiente». Sin él, una
          empresa que nace acá se veía igual que una que todavía no cargó sus
          saldos — y el tablero se lo iba a pedir todos los días, para siempre. */}
      {cargada === false && sinSaldos !== null && (
        <section className="card">
          <div className="card__head"><h3>{t('ap.noPrior')}</h3></div>
          <p className="small">{t('ap.noPriorWhy', { desde: sinSaldos })}</p>
        </section>
      )}

      {cargada === false && sinSaldos === null && !escribe && (
        <div className="alert alert--warn"><span>{t('sb.SB025')}</span></div>
      )}

      {/* ▶ La salida para quien no tiene nada que cargar, al lado del formulario
          y no escondida: es el caso de TODA empresa que se constituye ahora, y
          hasta hoy no tenía cómo decirlo. */}
      {cargada === false && sinSaldos === null && administra && (
        <section className="card" style={{ marginTop: 'var(--s6)' }}>
          <div className="card__head"><h3>{t('ap.noPriorTitle')}</h3></div>
          <p className="small">{t('ap.noPriorHelp')}</p>
          <div className="row" style={{ padding: '0 var(--s7) var(--s7)', gap: 'var(--s4)' }}>
            <button
              className="btn btn--secondary" type="button" disabled={ocupado || !fechaValida}
              onClick={() => void declarar()}
            >
              {t('ap.noPriorDo')}
            </button>
            <span className="small muted">{t('ap.noPriorNote')}</span>
          </div>
        </section>
      )}

      {cargada === false && escribe && (
        <>
          {/* La fecha es de las dos vias: el asiento de apertura es el mismo
              hecho, venga de la plantilla o del balance ajeno. */}
          <section className="card" style={{ marginBottom: 'var(--s6)' }}>
            <label className="field">
              <span className="field__label">{t('ap.date')}</span>
              <input
                className="input input--mono" value={fecha}
                onChange={(e) => setFecha(e.target.value.trim())}
              />
              <span className="field__hint">{t('ap.dateHint')}</span>
            </label>

            <div className="card__head" style={{ padding: 'var(--s6) 0 0' }}>
              <h3>{t('mp.via')}</h3>
            </div>
            <div className="grid grid--2" style={{ gap: 'var(--s5)', marginTop: 'var(--s5)' }}>
              {([
                ['plantilla', 'mp.viaPlantilla', 'mp.viaPlantillaWhy'],
                ['balance', 'mp.viaBalance', 'mp.viaBalanceWhy'],
              ] as const).map(([valor, titulo, porque]) => (
                <label
                  key={valor}
                  className={via === valor ? 'card card--sel' : 'card'}
                  style={{ cursor: 'pointer' }}
                >
                  <span style={{ display: 'flex', gap: 'var(--s3)', alignItems: 'center' }}>
                    <input
                      type="radio" name="via" value={valor} checked={via === valor}
                      onChange={() => { setVia(valor); setCsv(''); }}
                    />
                    <strong>{t(titulo)}</strong>
                  </span>
                  <p className="small muted">{t(porque)}</p>
                </label>
              ))}
            </div>
          </section>
        </>
      )}

      {cargada === false && escribe && via === 'balance' && (
        <DesdeBalance
          pedir={pedir}
          fecha={fechaValida ? fecha : ''}
          escribe={escribe && fechaValida}
          alCargar={(n) => { setNumero(n); void mirar(); }}
        />
      )}

      {cargada === false && escribe && via === 'plantilla' && (
        <>
          {/* ▶ LA PLANTILLA SE DESCARGA ACÁ, Y HASTA HOY NO SE DESCARGABA EN
              NINGUNA PARTE.
              La tarjeta de al lado decía «es la que se descarga desde el
              catálogo» y en el catálogo no hay ninguna descarga: la plantilla
              existía sólo como comando nuestro. O sea que el cliente que
              elegía esta vía tenía que escribirnos para pedirla — la llamada
              que E8 vino a quitar, en la última pantalla del arranque.
              Va acá y no en el catálogo porque acá es donde está parado quien
              la necesita: mandarlo a otra pantalla a buscarla y volver es un
              viaje que no compra nada. */}
          <section className="card" style={{ marginBottom: 'var(--s6)' }}>
            <div className="row row--tight" style={{ marginBottom: 'var(--s6)' }}>
              <span>
                <strong>{t('ap.templateTitle')}</strong>
                <br />
                <span className="small muted">{t('ap.templateWhy')}</span>
              </span>
              <span className="spacer" />
              <button
                className="btn btn--secondary btn--sm" type="button"
                disabled={cuentasDeMovimiento.length === 0}
                onClick={descargarPlantilla}
              >
                {t('ap.templateGet')}
              </button>
            </div>

            <label className="field">
              <span className="field__label">{t('ap.file')}</span>
              <input
                className="input" type="file" accept=".csv,text/csv"
                onChange={(e) => void leerArchivo(e.target.files?.[0])}
              />
              <span className="field__hint">{t('ap.columns')}</span>
            </label>

            <label className="field" style={{ marginTop: 'var(--s5)' }}>
              <span className="field__label">{t('ap.paste')}</span>
              <textarea
                className="input input--mono" rows={6} value={csv}
                onChange={(e) => setCsv(e.target.value)}
              />
              <span className="field__hint">{t('ap.pasteHint')}</span>
            </label>
          </section>

          {/* ── Lo que se leyó, o por qué no se pudo ── */}
          {lectura && !lectura.ok && (
            <div className="alert alert--warn" style={{ marginBottom: 'var(--s6)' }}>
              {/* El lector devuelve una CLAVE, no una frase: la frase es de
                  este diccionario y existe en los dos idiomas. */}
              <span>
                {lectura.fila ? `${t('ap.row')} ${lectura.fila}: ` : ''}
                {t(`ap.err.${lectura.motivo}` as never)}
                {lectura.dato ? ` — ${lectura.dato}` : ''}
              </span>
            </div>
          )}

          {lectura?.ok && (
            <section className="card card--pad0" style={{ marginBottom: 'var(--s6)' }}>
              <div className="card__head">
                <h3>{t('ap.preview')}</h3>
                <span className="spacer" />
                <span className="small muted">
                  {lineas.length === 1
                    ? t('ap.lineCountOne')
                    : t('ap.lineCount', { lineas: String(lineas.length) })}
                </span>
              </div>

              <div className="table-wrap">
                <table className="tbl">
                  <thead>
                    <tr>
                      <th>{t('ap.account')}</th>
                      <th style={{ textAlign: 'right' }}>{t('ap.debit')}</th>
                      <th style={{ textAlign: 'right' }}>{t('ap.credit')}</th>
                      <th>{t('ap.detail')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lineas.map((l, i) => (
                      <tr key={`${l.cuenta}-${i}`}>
                        <td className="mono">{l.cuenta}</td>
                        {/* Tal cual vinieron. Ver la cabecera. */}
                        <td className="mono" style={{ textAlign: 'right' }}>{l.debe ?? ''}</td>
                        <td className="mono" style={{ textAlign: 'right' }}>{l.haber ?? ''}</td>
                        <td className="small">
                          {l.detalle ?? ''}
                          {l.clasificacion && (
                            <span className="muted small">
                              {' '}{Object.entries(l.clasificacion)
                                .map(([k, v]) => `${k}: ${v}`).join(' · ')}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="card__foot">
                {cuadre.que === 'ilegible' && (
                  // ▶ `alert` y no `chip`: un chip es un RÓTULO corto y no
                  // envuelve, así que esta frase —más el código de cuenta—
                  // desplazaba la página de lado en una pantalla angosta.
                  // Ver el comentario de `.chip` en componentes.css.
                  <span className="alert alert--warn alert--inline">
                    <span>{t('ap.unreadable')} — {cuadre.cuenta}</span>
                  </span>
                )}
                {cuadre.que === 'calculado' && (
                  <span className={cuadre.cuadra ? 'chip chip--ok' : 'chip chip--warn'}>
                    {t('ap.debit')} {mostrar(cuadre.debe, idioma)}
                    {' · '}{t('ap.credit')} {mostrar(cuadre.haber, idioma)}
                    {!cuadre.cuadra && cuadre.falta
                      && ` · ${t('ap.difference')} ${mostrar(cuadre.falta, idioma)}`}
                  </span>
                )}
                <span className="small muted">{t('ap.previewNote')}</span>
              </div>
            </section>
          )}

          <div className="page-actions">
            <button
              className="btn btn--primary" type="button"
              disabled={!puedeMandar}
              onClick={() => void mandar()}
            >
              {t('ap.load')}
            </button>
            <span className="small muted">{t('ap.onlyOnce')}</span>
          </div>
        </>
      )}
    </div>
  );
}
